// ===================================================================
// nextSchedule.js  (ESM / src 正本)
// computeNextSchedule(blocks, config) -> [{ blockId, matchKey, teamA, teamB, order }]
//
// ★src/App.js L5142-5252 の nextSchedule useMemo body を1文字もロジック変更せず純関数化。
//   ロジック (FIXED_NEXT_COUNT=3 / 進行中1.0 / soft回避2パス / virtualDone / sort / 入口ガード) は完全保存。
// ★src/shared が正 (ESM)。functions/shared へは predeploy で CJS変換コピー。
// ===================================================================

export function computeNextSchedule(blocks, config) {
  // 入口ガード (異常入力でのみ早期return・正常時は素通り = 本体ロジック不変)
  if (!Array.isArray(blocks)) return [];
  const { ROBIN_ORDER, MATCHES_PER_BLOCK } = config || {};
  if (!ROBIN_ORDER || !MATCHES_PER_BLOCK) return [];

  // Step7+8: 進行順スケジューラ v2
  //   NEXT 表示数は 3 固定
  //   進行中試合は進行率上「完了同等 (1.0)」として扱う (Step7)
  //   進行中プレイヤーを含む試合は NEXT 候補から soft 回避 (Step8)
  const FIXED_NEXT_COUNT = 3;

  // (1) アクティブブロック (確定済み除外 + ROBIN_ORDER 不在ペア数除外 + 完了済み除外)
  const activeBlocks = blocks.filter(b => {
    if (b.confirmed) return false;
    if (!ROBIN_ORDER[b.pairs.length]) return false;
    const total = MATCHES_PER_BLOCK[b.pairs.length] || 0;
    const done = Object.keys(b.scores || {}).length;
    if (done >= total) return false;
    const order = ROBIN_ORDER[b.pairs.length];
    const hasNextMatch = order.some(([a, bb]) => {
      const key = `${Math.min(a, bb)}-${Math.max(a, bb)}`;
      if (key in (b.scores || {})) return false;
      if (b.activeMatches?.[key]) return false;
      return true;
    });
    return hasNextMatch;
  });

  if (activeBlocks.length === 0) return [];

  // (2) 仮想進行状態の初期化
  const virtualDone = {};

  // (3) Step8: ブロック内で「進行中プレイヤーを含む試合」を判定するヘルパー
  //   blockごとに、現在 activeMatches に登場しているチーム番号集合を作る
  //   その集合と試合の (a, b) が重なれば「進行中プレイヤーを含む」
  const activeTeamsByBlock = {};
  blocks.forEach(b => {
    const set = new Set();
    Object.keys(b.activeMatches || {}).forEach(key => {
      const [x, y] = key.split('-').map(s => parseInt(s, 10));
      if (Number.isFinite(x)) set.add(x);
      if (Number.isFinite(y)) set.add(y);
    });
    activeTeamsByBlock[b.id] = set;
  });

  // (4) NEXT を順番に取り出す
  const schedule = [];
  for (let slot = 0; slot < FIXED_NEXT_COUNT; slot++) {
    // (4.1) 各ブロックの仮想進行率を計算
    const candidates = activeBlocks.map(b => {
      const total = MATCHES_PER_BLOCK[b.pairs.length] || 0;
      const done = Object.keys(b.scores || {}).length;
      const inProgress = Object.keys(b.activeMatches || {}).length;
      const adjustedDone = done + inProgress;  // 進行中=1.0 (Step7)
      const virtualAdded = virtualDone[b.id] || 0;
      const virtualProgress = total > 0 ? (adjustedDone + virtualAdded) / total : 1;
      return { block: b, virtualProgress, total, done, inProgress, adjustedDone };
    });

    // (4.2) ソート: raw progress 低い順 > 残り多い順 > ペア数多い順 > ID昇順
    candidates.sort((a, b) => {
      if (a.virtualProgress !== b.virtualProgress) return a.virtualProgress - b.virtualProgress;
      const aRem = a.total - a.adjustedDone - (virtualDone[a.block.id] || 0);
      const bRem = b.total - b.adjustedDone - (virtualDone[b.block.id] || 0);
      if (bRem !== aRem) return bRem - aRem;
      if (b.block.pairs.length !== a.block.pairs.length) return b.block.pairs.length - a.block.pairs.length;
      return a.block.id.localeCompare(b.block.id);
    });

    // (4.3) Step8: 2パス探索で進行中プレイヤー soft 回避
    //   1st pass: 進行中プレイヤーを含まない試合を優先的に選ぶ
    //   2nd pass: 1st pass で見つからなければ、進行中プレイヤーを含む試合も許可
    let picked = null;

    // ヘルパー: 指定 block の試合候補を探す (avoidActivePlayer=true なら soft 回避)
    const findNextInBlock = (block, avoidActivePlayer) => {
      const order = ROBIN_ORDER[block.pairs.length];
      const activeTeams = activeTeamsByBlock[block.id] || new Set();
      for (const [a, bb] of order) {
        const key = `${Math.min(a, bb)}-${Math.max(a, bb)}`;
        if (key in (block.scores || {})) continue;
        if (block.activeMatches?.[key]) continue;
        if (schedule.some(s => s.blockId === block.id && s.matchKey === key)) continue;
        // Step8: 1st pass では active player を含む試合をスキップ
        if (avoidActivePlayer && (activeTeams.has(a) || activeTeams.has(bb))) continue;
        return { blockId: block.id, matchKey: key, teamA: a, teamB: bb };
      }
      return null;
    };

    // 1st pass: 進行中プレイヤーを含まない試合を全候補ブロックで探す
    for (const cand of candidates) {
      picked = findNextInBlock(cand.block, true);
      if (picked) break;
    }

    // 2nd pass: 1st pass で見つからなければ、進行中プレイヤーを含む試合も許可
    if (!picked) {
      for (const cand of candidates) {
        picked = findNextInBlock(cand.block, false);
        if (picked) break;
      }
    }

    if (!picked) break;

    schedule.push({ ...picked, order: slot + 1 });
    virtualDone[picked.blockId] = (virtualDone[picked.blockId] || 0) + 1;
  }

  return schedule;
}